import { origin } from '../BackBar';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@verlo/shared/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { resolveTableState, turnStatus, sentence } from '../../lib/fohState';
import { FloorFeature } from '@verlo/shared/components/floorplan/FloorFeature';
import { floorTables, isOutOfUse, joinedBehind } from '../../lib/tonight';
import { OFF_HATCH } from './TableCard';

// The state, as the classes a shape actually uses: a 2px border and a faint fill so the
// plan reads as a heat-map at a glance. The language's tones carry the meaning — navy is
// "held for someone" (reserved), the two neutrals are a party that's fine (seated, then
// ordering, a shade firmer), copper is food in play (time), claret is a bill waiting
// (money) — so the plan and the table cards and the tickets all say the same thing with
// the same colour. Every class is a literal so Tailwind keeps it.
const SHAPE_STYLE = {
  free:     { border: 'border-hairline',           fill: 'bg-white' },
  reserved: { border: 'border-verlo-hover',        fill: 'bg-verlo-tint' },
  seated:   { border: 'border-ink-faint',          fill: 'bg-white' },
  ordering: { border: 'border-ink-soft',           fill: 'bg-surface-2' },
  starters: { border: 'border-signal-warn-line',   fill: 'bg-signal-warn-tint' },
  mains:    { border: 'border-signal-warn-line',   fill: 'bg-signal-warn-tint' },
  dessert:  { border: 'border-signal-warn-line',   fill: 'bg-signal-warn-tint' },
  bill:     { border: 'border-signal-stop-line',   fill: 'bg-signal-stop-tint' },
  cleaning: { border: 'border-hairline',           fill: 'bg-paper' },
  off:      { border: 'border-hairline',           fill: OFF_HATCH },
};

const LEGEND = [
  { label: 'free', dot: 'bg-white ring-1 ring-inset ring-hairline' },
  { label: 'reserved', dot: 'bg-verlo-hover' },
  { label: 'seated', dot: 'bg-ink-faint' },
  { label: 'ordering', dot: 'bg-ink-soft' },
  { label: 'food', dot: 'bg-signal-warn-line' },
  { label: 'bill', dot: 'bg-signal-stop-line' },
  { label: 'cleaning', dot: 'bg-paper ring-1 ring-inset ring-hairline' },
];

// A table with no size of its own is drawn by its capacity, as before. One that has been
// resized carries its box in percent of the plan (backend/floorplan.py `table_geometry`).
const autoWidth = (capacity) => (capacity <= 2 ? 58 : capacity <= 4 ? 68 : capacity <= 6 ? 80 : 94);
// Whatever the room's scale, a table is never smaller than a finger: 56 × 44 (FOH UX
// review, wave 3). A resized table keeps its percent box above that floor.
const MIN_TABLE = { minWidth: 56, minHeight: 44 };
const tableBox = (t) => (t.width > 1 && t.height > 1
  ? { width: `${t.width}%`, height: `${t.height}%`, ...MIN_TABLE }
  : { width: `${autoWidth(t.capacity)}px`, height: `${Math.round(autoWidth(t.capacity) * 0.72)}px` });
const isPlaced = (t) => t.position_x > 0 || t.position_y > 0;

// Features that do things (FOH plan round two, F4): on the live plan, the bar opens the
// bar's board of tickets, the kitchen the pass, the host stand a walk-in.
const FEATURE_LINK = {
  bar: { to: '/bar-board', title: 'Open the bar board' },
  kitchen: { to: '/kitchen', title: 'Open the kitchen display' },
  host_stand: { to: '/bookings?walkin=1', title: 'Seat a walk-in' },
};

// The room as service sees it (Oct 2026: the plan is set up in the back office, under
// Admin › The tills › Floor plan, and this till applies it). A tap seats or opens a table;
// a manager holds one for tonight's changes (out of use, joined: TonightSheet).
export const FloorplanView = ({
  tables,
  features = [],
  filteredTables,
  selectedSection = 'all',
  handleTableClick,
  onHold,
}) => {
  const navigate = useNavigate();
  const planRef = useRef(null);
  // The room fills the screen below its controls, and no more: sized as 100vh less a fixed
  // 15rem it ran a hundred-odd pixels past the bottom of a tablet, so the door and the
  // toilets were always a scroll away. Measured from where it starts, kept on resize.
  const [fitHeight, setFitHeight] = useState(null);
  useEffect(() => {
    const fit = () => {
      const top = planRef.current?.getBoundingClientRect().top;
      if (top != null) setFitHeight(Math.max(480, Math.round(window.innerHeight - top - 24)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // Holding a table (a manager's): tonight's changes. A hold that fired swallows its click.
  const hold = useRef(null);
  const holdStart = (table) => {
    if (!onHold) return;
    hold.current = setTimeout(() => { hold.current = 'fired'; onHold(table); }, 600);
  };
  const holdEnd = () => {
    if (hold.current && hold.current !== 'fired') { clearTimeout(hold.current); hold.current = null; }
  };

  const inSection = (item) => selectedSection === 'all' || (item.section || 'Main') === selectedSection;
  const shownFeatures = features.filter(inSection);
  const placed = filteredTables.filter(isPlaced);
  const unplaced = filteredTables.filter((t) => !isPlaced(t));
  // A join is seated as one: a tap on any of its tables seats or opens the lead, "T5 + T6".
  const asFloor = new Map(floorTables(tables).map((t) => [t.id, t]));
  const leadOf = (t) => asFloor.get(t.joined?.lead || t.id) || t;

  return (
    <div className="space-y-3" data-testid="floorplan-view">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto no-scrollbar">
          {unplaced.length > 0 && (
            <span className="shrink-0 whitespace-nowrap text-xs text-ink-soft" data-testid="floorplan-unplaced">
              Not on the plan: {unplaced.map((t) => t.name).join(', ')}
            </span>
          )}
        </div>
        <div className="hidden lg:flex shrink-0 items-center gap-x-3 text-[11px] text-ink-faint" data-testid="floorplan-legend">
          {LEGEND.map((l) => (
            <span key={l.label} className="flex items-center gap-1">
              <span className={`h-2 w-2 rounded-full ${l.dot}`} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      <Card
        className="bg-surface-2 border border-hairline h-[calc(100vh-15rem)] min-h-[480px] relative overflow-hidden select-none"
        style={fitHeight ? { height: fitHeight } : undefined}
      >
        <div className="absolute inset-0" ref={planRef}>
          {/* The room's furniture — kitchen, bar, entrance, whatever the venue drew.
              Data, not hard-coded rectangles (backend/floorplan.py); tables draw on top. */}
          {shownFeatures.map((f) => (
            <FloorFeature
              key={f.id}
              feature={f}
              className={FEATURE_LINK[f.kind] ? 'cursor-pointer hover:ring-2 hover:ring-verlo/40 transition-shadow' : ''}
              title={FEATURE_LINK[f.kind] ? FEATURE_LINK[f.kind].title : undefined}
              onClick={() => { if (FEATURE_LINK[f.kind]) navigate(FEATURE_LINK[f.kind].to, { state: origin(`/tables${window.location.search}`, 'Floor plan') }); }}
            />
          ))}

          {placed.map((table) => {
            const lead = leadOf(table);
            const st = resolveTableState(lead);
            const shape = SHAPE_STYLE[st.key] || SHAPE_STYLE.free;
            const occupied = String(lead.status || '').toLowerCase() === 'occupied';
            const over = turnStatus(lead)?.over;
            // One corner mark: an allergy (safety, rose) wins over a blown turn time (amber).
            const attention = lead.has_allergies ? 'allergy' : over ? 'over' : null;
            const round = table.shape === 'round';
            const behind = joinedBehind(table);
            const joined = !!table.joined;

            return (
              <div
                key={table.id}
                className={`absolute flex flex-col items-center justify-center border-2 ${round ? 'plan-round' : 'rounded-xl'}
                  ${joined ? 'border-dashed border-verlo-hover' : shape.border} ${st.key === 'free' ? 'bg-white' : shape.fill}
                  cursor-pointer shadow-sm transition-transform hover:scale-105 hover:shadow-md`}
                style={{
                  left: `${table.position_x}%`,
                  top: `${table.position_y}%`,
                  ...tableBox(table),
                  transform: 'translate(-50%, -50%)',
                  zIndex: 20,
                }}
                onPointerDown={() => holdStart(table)}
                onPointerUp={holdEnd}
                onPointerLeave={holdEnd}
                onContextMenu={(e) => e.preventDefault()}
                onClick={() => {
                  if (hold.current === 'fired') { hold.current = null; return; }
                  handleTableClick(isOutOfUse(table) ? table : lead);
                }}
                data-testid={`table-map-${table.name}`}
              >
                <span className="font-mono text-[13px] font-bold leading-none text-ink">{table.name}</span>
                <span className="mt-0.5 font-mono text-[10px] leading-none text-ink-soft">
                  {behind ? `with ${lead.own_name || ''}`.trim() : occupied ? `${lead.covers}/${lead.capacity}` : lead.capacity}
                </span>
                <span className={`mt-0.5 text-[9px] leading-none ${st.text}`}>{behind ? 'Joined' : sentence(st.word)}</span>

                {attention && !behind && (
                  <span
                    className={`absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full text-white ${
                      attention === 'allergy' ? 'bg-signal-stop-line' : 'bg-signal-warn-line'
                    }`}
                    title={attention === 'allergy' ? (lead.allergy_notes || 'Allergy') : 'Over turn time'}
                  >
                    {attention === 'allergy'
                      ? <AlertTriangle size={9} />
                      : <span className="text-[10px] font-bold leading-none">!</span>}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default FloorplanView;
